/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React from 'react';
import { Controller, useForm, Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { zodResolver } from '@hookform/resolvers/zod';
import { apiLocation } from '../../../../config/env.json';
import { signUpLocal } from '../../redux/actions';
import { selectSignUpLoading } from '../../redux/selectors';
import { Spacer } from '../helpers';

import Button from '../ui/Button';
import { ButtonTypes } from '../ui/Button/button-types';
import Input from '../ui/Input';
import InputPassword from '../ui/input-password';

import CheckedHome from '../../assets/icons/checked-home';
import GoogleIcon from '../../assets/icons/google-icon';
import Marker from '../../assets/icons/marker';
import { SignUpSchema, signUpSchema } from './signup.schema';

const mapStateToProps = createSelector(
  selectSignUpLoading,
  (signUpLoading: boolean) => ({
    signUpLoading
  })
);

const mapDispatchToProps = {
  signUpLocal
};

const resolver = zodResolver(signUpSchema) as Resolver<SignUpSchema>;

function SignUp({
  signUpLocal,
  signUpLoading
}: {
  readonly signUpLocal: (formValues: SignUpSchema) => void;
  readonly signUpLoading: boolean;
}) {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
    control
  } = useForm<SignUpSchema>({
    resolver,
    defaultValues: {
      name: '',
      phone: '',
      cpf: '',
      email: '',
      password: ''
    }
  });

  const onSubmit = (data: SignUpSchema): void => {
    const normalizedData = {
      ...data,
      email: data.email.toLowerCase().trim()
    };
    signUpLocal(normalizedData);
  };

  const signUpBenefits = ['sign-up-benefits1', 'sign-up-benefits3'];

  return (
    <>
      <section className='info-section'>
        <p id='main-title'>{t('register.headings.sign-up-title')}</p>
        <Spacer size='large' />
        <div id='secundary-title-container'>
          <span className='secondary-title'>
            {t('register.headings.sign-up-subtitle01')}
          </span>
          <span className='secondary-title' id='text-marker'>
            <Marker id='marker' />
            {t('register.headings.sign-up-subtitle02')}
          </span>
          <span className='secondary-title'>
            {t('register.headings.sign-up-subtitle03')}
          </span>
        </div>
        <Spacer size='large' />
        {signUpBenefits.map(key => (
          <div key={key} className='register-benefits'>
            <CheckedHome />
            <p>{t(`register.headings.${key}`)}</p>
          </div>
        ))}
      </section>

      <section className='register-section'>
        <a className='google-container' href={`${apiLocation}/signin-google`}>
          <GoogleIcon className='google-icon' />
          <p>{t('register.buttons.sign-up-google')}</p>
        </a>
        <Spacer size='medium' />
        <p id='google-or-local'>Ou</p>
        <Spacer size='medium' />
        <form onSubmit={handleSubmit(onSubmit) as () => void}>
          <Input
            {...register('name')}
            placeholder='Nome completo'
            hasError={!!errors.name}
            messageError={errors.name?.message}
          />
          <Input
            {...register('phone')}
            placeholder='Telefone'
            hasError={!!errors.phone}
            messageError={errors.phone?.message}
            hasMask={true}
            maskValue='(99) 99999-9999'
          />
          <Input
            {...register('cpf')}
            placeholder='CPF'
            hasError={!!errors.cpf}
            messageError={errors.cpf?.message}
            hasMask={true}
            maskValue='999.999.999-99'
          />
          <Input
            {...register('email')}
            type='email'
            placeholder='E-mail'
            hasError={!!errors.email}
            messageError={errors.email?.message}
          />
          <Controller
            control={control}
            name='password'
            render={({ field: { onChange, value } }) => (
              <InputPassword
                value={value}
                onChange={onChange}
                placeholder='Senha'
                hasError={!!errors.password}
                messageError={errors.password?.message}
              />
            )}
          />
          <div className='form-btn'>
            <Button
              type='submit'
              buttonType={ButtonTypes.Primary}
              style={{ height: '48px', width: '100%' }}
              isLoading={signUpLoading}
            >
              {t('register.buttons.new-account')}
            </Button>
          </div>
        </form>
        <Spacer size='medium' />
        <p id='info-terms'>{t('register.headings.sign-up-accept-terms')}</p>
      </section>
    </>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(SignUp);
